import React, { useEffect, useRef } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { useState } from "react";
import RecordAskPermission from "./RecordAskPermission.web";
import RecordBottomBar from "./RecordBottomBar";
import RecordPreview from "./RecordPreview.web";
import { useDispatch, useSelector } from "react-redux";
import { getFilesToUpload } from "@microdoing/shared/services/upload/selectors";
import {
  addFileToUpload,
  resetUpload,
} from "@microdoing/shared/services/upload/actions";
import { Box, Loading } from "app/components/common";
import { MediaRecorder } from "extendable-media-recorder";
import AudioRecordingBar from "../ReflexiveAnalysis/ReflexiveAnalysisScreen/AudioRecordingBar";
import { useIsFocused } from "@react-navigation/native";

const EVENT_NAME = "dataavailable";

const requestRecorder = async () => {
  try {
    const stream = await navigator?.mediaDevices?.getUserMedia({ audio: true });

    return new MediaRecorder(stream, { mimeType: "audio/wav" });
  } catch (error) {
    console.log(error);
  }
};

interface Props {
  recordType: "camera" | "audio" | "screen";
  onClick?: () => void;
  onSubmit: () => void;
  onClearRecording?: () => void;
  requesting?: boolean;
}

// TODO : fix Error: "There is already an encoder stored which handles exactly the same mime types.""
// TODO - change hook "connect" in the react-media-recorder  https://github.com/chrisguttandin/extendable-media-recorder/issues/637

const RecordMediaBlock = ({
  recordType = "camera",
  onClick,
  onClearRecording = () => null,
  onSubmit,
  requesting = false,
}: Props) => {
  const [video, setVideo] = useState(null);
  const [audio, setAudio] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecordingVisible, setIsRecordingVisible] = useState(false);
  const [mediaStatus, setMediaStatus] = useState("");
  const [recorder, setRecorder] = useState<any>(null);

  const fileToUploads = useSelector(getFilesToUpload);
  const fileToUpload = fileToUploads?.[0] || {};

  const dispatch = useDispatch();

  const onStop = (blobUrl, blob) => {
    setIsPlaying(false);
    if (recordType === "audio") {
      setAudio(blobUrl);
    } else {
      setVideo(blobUrl);
    }
    dispatch(
      addFileToUpload({
        file: blobUrl,
        uri: blobUrl,
        fileName: recordType === "audio" ? "recording.wav" : "recording.mp4",
        fileType: recordType === "audio" ? "audio/wav" : "video/mp4",
      }),
    );
  };

  const {
    status,
    startRecording,
    pauseRecording,
    stopRecording,
    resumeRecording,
    clearBlobUrl,
    previewStream,
  } = useReactMediaRecorder({
    video: recordType === "camera",
    screen: recordType === "screen",
    audio: true,
    onStop,
  });

  const videoRef = useRef<HTMLVideoElement>(null);

  const reset: any = async () => {
    stopRecording();
    setIsPlaying(false);
    clearBlobUrl();

    recorder?.stop();

    const tracks = previewStream?.getVideoTracks();
    tracks?.map(mediaStreamTrack => mediaStreamTrack?.stop());
    await navigator?.mediaDevices
      ?.getUserMedia({ audio: true })
      .then(stream => {
        stream?.getTracks().forEach(track => {
          track.stop();
          stream.removeTrack(track);
        });

        stream?.getAudioTracks().forEach(function (track) {
          track.stop();
        });

        stream?.getVideoTracks().forEach(function (track) {
          track.stop();
        });
      });

    await navigator?.mediaDevices
      ?.getUserMedia({ audio: true, video: true })
      .then(stream => {
        stream.getTracks().forEach(track => {
          track.stop();
          stream.removeTrack(track);
        });

        stream.getAudioTracks().forEach(function (track) {
          track.stop();
        });

        stream.getVideoTracks().forEach(function (track) {
          track.stop();
        });
      });

    recorder?.removeEventListener(EVENT_NAME, handleData);
    setMediaStatus("");
    setVideo(null);
    setAudio(null);
    setRecorder(null);
    dispatch(resetUpload());
  };

  useEffect(() => {
    // to stream preview update ref for video
    if (videoRef.current && previewStream && recordType !== "audio") {
      videoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  useEffect(() => {
    setMediaStatus(status);
    if (recordType !== "audio") {
      if (status === "recording" && mediaStatus === "idle") {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
        }

        videoRef?.current?.load();

        // We want to wait before starting the record for showing a preview - user resume manually
        if (recordType === "camera") {
          pauseRecording();
        }
      }
    }
  }, [status]);

  useEffect(() => {
    if (!recorder && recordType === "audio") {
      requestRecorder().then((media: any) => setRecorder(media));
    }
  }, []);

  const isFocused = useIsFocused();

  useEffect(() => {
    if (!isFocused) {
      setIsPlaying(false);
    }
  }, [isFocused]);

  const handleStartRecording = async () => {
    setIsRecordingVisible(true);
    startRecording();
    setIsPlaying(true);
  };

  const onChangeRecording = () => {
    if (mediaStatus === "recording") {
      stopRecording();
      setIsPlaying(false);
    } else if (mediaStatus === "paused") {
      setIsPlaying(true);
      resumeRecording();
    } else if (mediaStatus === "stopped") {
      handleCloseRecording();
    } else {
      setMediaStatus("recording");
      setIsPlaying(true);
    }
  };

  const handleCloseRecording = () => {
    setIsRecordingVisible(false);
    clearBlobUrl();
    reset();
    onClearRecording();
  };

  const handleData = e => {
    const file = new Blob([e.data], { type: "audio/wav" });
    const url = URL.createObjectURL(file);
    onStop(url, null);
  };

  useEffect(() => {
    if (!recorder) return;

    if (isPlaying && recordType === "audio") {
      recorder.start();
      setMediaStatus("recording");
      setIsPlaying(true);
    } else if (!isPlaying && recordType === "audio") {
      recorder.stop();
      setMediaStatus("stopped");
      setIsPlaying(false);
    }

    recorder.addEventListener(EVENT_NAME, handleData);
    return () => recorder.removeEventListener(EVENT_NAME, handleData);
  }, [recorder, isPlaying]);

  return (
    <Box
      width="100%"
      position="relative"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      flex={1}
      height="100%"
    >
      <Box
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        overflow="hidden"
        maxWidth={700}
        width="100%"
        minHeight={300}
        height="auto"
        position="relative"
        borderRadius={4}
      >
        {!isPlaying && !isRecordingVisible && (
          <RecordAskPermission
            onClick={handleStartRecording}
            recordType={recordType}
          />
        )}
        {(mediaStatus === "idle" ||
          mediaStatus === "acquiring_media" ||
          mediaStatus === "paused") &&
          !previewStream &&
          !recorder &&
          isPlaying && <Loading size={30} backgroundWhite />}

        {((!!previewStream && recordType !== "audio") || !!audio) &&
          isRecordingVisible && (
            <RecordPreview
              previewStream={previewStream}
              video={video}
              videoRef={videoRef}
              audioRecord={audio}
              isAudio={recordType === "audio"}
            />
          )}
        {isRecordingVisible && isPlaying && recordType === "audio" && (
          <AudioRecordingBar hasDarkBackground />
        )}
        {(!!previewStream || !!audio) && isRecordingVisible && (
          <RecordBottomBar
            requesting={requesting}
            mediaStatus={mediaStatus}
            onClick={onChangeRecording}
            onClickSubmit={onSubmit}
          />
        )}
      </Box>
    </Box>
  );
};

export default RecordMediaBlock;
